import React from 'react'
import { useIntl } from 'react-intl'
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
} from '@mui/material'
import { withStyles } from '@mui/styles'
import { basicTableStyles } from './basic-table.styles'

/**
 * @typedef {object} ColumnConfig
 * @property {string} label - El texto que se mostrará en la cabecera de la columna.
 * @property {string} key - La clave para acceder al valor correspondiente en el objeto de datos de la fila.
 * @property {'left' | 'right' | 'center'} [align='left'] - La alineación horizontal del contenido de la celda.
 * @property {'date' | 'datetime' | 'component' | 'responsable' | 'comboDocumentData' | 'comboData'} [type] - El tipo de dato para aplicar un formato especial o renderizar un componente.
 * @property {React.ComponentType<{value: any, context: any, row: object}>} [component] - El componente de React a renderizar si el `type` es 'component'. Recibirá `value`, `context` y `row` como props.
 */

const defaultKey = (i, row) => i

/**
 * Un componente de tabla configurable construido con Material-UI y estilizado con JSS.
 *
 * Renderiza dinámicamente columnas y filas basándose en un objeto de configuración y un array de datos.
 * Se integra con `react-intl` para formatear fechas, horas y cadenas internacionalizadas (i18n).
 * Soporta renderizado de celdas personalizadas, manejadores de clic en filas y estados de carga.
 *
 * @component
 * @example
 * // Componente personalizado para renderizar en una celda.
 * const StatusChip = ({ value }) => (
 * <span style={{ color: value === 'active' ? 'green' : 'red' }}>
 * {value.toUpperCase()}
 * </span>
 * );
 *
 * const tableData = [
 * { id: 1, product: 'Laptop', created: '2025-10-20T10:00:00Z', status: 'active' },
 * { id: 2, product: 'Mouse', created: '2025-10-19T15:30:00Z', status: 'inactive' },
 * ];
 *
 * const tableConfig = [
 * { label: 'Producto', key: 'product' },
 * { label: 'Fecha de Creación', key: 'created', type: 'datetime', align: 'right' },
 * { label: 'Estado', key: 'status', type: 'component', component: StatusChip, align: 'center' },
 * ];
 *
 * const handleRowClick = (row) => {
 * console.log('Fila clickeada:', row.id);
 * };
 *
 * // Este componente debe estar envuelto en un <IntlProvider> de react-intl.
 * <BasicTable
 * data={tableData}
 * config={tableConfig}
 * rowKey={(i, row) => row.id}
 * onClick={handleRowClick}
 * />
 *
 * @param {object} props - Las props del componente BasicTable.
 * @param {function(number, object): (string|number)} [props.rowKey=defaultKey] - Función para generar una clave única para cada fila. Recibe el índice y el objeto de la fila. Por defecto, usa el índice.
 * @param {object} props.classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @param {ColumnConfig[]} props.config - Array de objetos de configuración que definen las columnas de la tabla.
 * @param {object[]} props.data - Array de objetos que representan los datos a mostrar en las filas de la tabla.
 * @param {any} [props.context] - Un objeto de contexto opcional que se pasa a los componentes de celda personalizados (cuando `type` es 'component').
 * @param {function} [props.getdata] - Prop recibida pero no utilizada actualmente en la lógica del componente.
 * @param {'small' | 'medium'} [props.size='medium'] - La densidad de la tabla, que afecta al padding de las celdas.
 * @param {function(object): void} [props.onClick] - Una función callback que se ejecuta cuando se hace clic en una fila. Recibe el objeto de datos de la fila. Si se proporciona, las filas serán interactivas.
 * @param {boolean} [props.loading] - Si es `true` y se ha proporcionado un `onClick`, la fila mostrará un estilo de carga.
 */
export const BasicTable = withStyles(basicTableStyles)(({
  rowKey = defaultKey,
  classes,
  config,
  data,
  context,
  getdata,
  size = 'medium',
  ...props
}) => {
  const { formatDate, formatTime, formatMessage } = useIntl()

  const getParsedValue = (type, value, Component, context, row) => {
    switch (type) {
      case 'date':
        return (
          <Typography variant="body2" color="textSecondary">
            {formatDate(new Date(value))}
          </Typography>
        )
      case 'datetime':
        return (
          <Typography variant="body2" color="textSecondary">
            {formatDate(new Date(value)) + ' - ' + formatTime(new Date(value))}
          </Typography>
        )
      case 'component':
        return <Component value={value} context={context} row={row} />
      case 'responsable':
        const finalValue = row[value] ? `${row[value]} (${row['rol_usuario']})` : null
        return finalValue
      case 'comboDocumentData':
        return (
          <Typography variant="body2" color="textSecondary">
            {formatMessage({ id: `pages.supervision.edit.documents.${value}` })}
          </Typography>
        )
      case 'comboData':
        return (
          <Typography variant="body2" color="textSecondary">
            {formatMessage({ id: `global.basic_table.${value}` })}
          </Typography>
        )
      default:
        return value
    }
  }
  return (
    <TableContainer>
      <Table className={classes.table} aria-label="simple table" size={size}>
        <TableHead>
          <TableRow>
            {config.map(({ align = 'left', label }, i) => (
              <TableCell key={i} align={align}>
                <Typography variant="subtitle1">{label}</Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, i) => (
            <TableRow
              key={rowKey(i, row)}
              className={
                props.onClick
                  ? props.loading
                    ? classes.loadingRow
                    : classes.clickableRow
                  : classes.row
              }
              hover={!!props.onClick}
              onClick={() => (props.onClick ? props.onClick(row) : null)}
            >
              {config.map(({ key, align = 'left', type, component }, i) => (
                <TableCell key={i} align={align}>
                  {getParsedValue(type, row[key], component, context, row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
})
